import { useState } from "react";
import { Eye } from "@certa/icons/components/Eye";
import { useIntl } from "react-intl";
import { PermissionTypes } from "@certa/types";
import {
  DropdownSubMenu,
  DropdownSubMenuTrigger,
  DropdownSubMenuContent
} from "@certa/catalyst/components/Dropdown";
import { DROPDOWN_MENU_WIDTH } from "./constants";
import { WrapperWithIcon } from "../common";
import { ImpersonationUsersList } from "./ImpersonationUsersList";
import { useEnableUserImpersonation } from "@certa/common/hooks/useEnableUserImpersonation";
import { useCheckPermission } from "@certa/common/utils/permissions/Chowkidar";
import { toCapital } from "@certa/common/utils/string";

import { getUsersByKind } from "@certa/common/utils/helper";
import type { ImpersonateeUserListType } from "@certa/common/components/Header/types";
import { useInstanceConfigQuery } from "@certa/queries/hooks/config.hook";

export const ImpersonateAsUser = () => {
  const intl = useIntl();

  const canUserViewAs = useEnableUserImpersonation();
  const hasPermissionToImpersonate = useCheckPermission(
    PermissionTypes.CAN_IMPERSONATE_OTHER_USERS
  );

  const { data: config } = useInstanceConfigQuery();

  const tenantName = toCapital(
    (config?.name as string) || "certa"
  ) as Capitalize<string>;

  const usersByKind = getUsersByKind(tenantName);

  const getSubMenuTitle = () =>
    intl.formatMessage({
      id: "loginPageInstances.viewAs",
      defaultMessage: "View As"
    });

  return hasPermissionToImpersonate && canUserViewAs ? (
    <DropdownSubMenu key="impersonate-as-user">
      <DropdownSubMenuTrigger>
        <WrapperWithIcon icon={<Eye />}>{getSubMenuTitle()}</WrapperWithIcon>
      </DropdownSubMenuTrigger>
      <DropdownSubMenuContent width={DROPDOWN_MENU_WIDTH}>
        {usersByKind.map(user => (
          <UsersByKind user={user} key={user.name} />
        ))}
      </DropdownSubMenuContent>
    </DropdownSubMenu>
  ) : null;
};

const UsersByKind = ({ user }: { user: ImpersonateeUserListType }) => {
  const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);

  const handleSubmenuOpenChange = (isOpen: boolean) => {
    setIsSubmenuOpen(isOpen);
  };

  return (
    <DropdownSubMenu onOpenChange={handleSubmenuOpenChange}>
      <DropdownSubMenuTrigger>
        <WrapperWithIcon>{user.name}</WrapperWithIcon>
      </DropdownSubMenuTrigger>
      <ImpersonationUsersList kind={user.kind} isOpen={isSubmenuOpen} />
    </DropdownSubMenu>
  );
};
